import type {
  CreditCardFormMessages,
  CreditCardType,
} from './creditCardTypes'

const CARD_LENGTHS: Record<CreditCardType, number> = {
  visa: 16,
  mastercard: 16,
  amex: 15,
  discover: 16,
}

export function detectCreditCardType(value: string): CreditCardType | null {
  const digits = digitsOnly(value)
  if (digits.startsWith('4')) return 'visa'
  if (digits.startsWith('34') || digits.startsWith('37')) return 'amex'

  const firstTwo = Number(digits.slice(0, 2))
  const firstFour = Number(digits.slice(0, 4))
  if (
    (digits.length >= 2 && firstTwo >= 51 && firstTwo <= 55) ||
    (digits.length >= 4 && firstFour >= 2221 && firstFour <= 2720)
  ) {
    return 'mastercard'
  }

  const firstThree = Number(digits.slice(0, 3))
  if (
    digits.startsWith('6011') ||
    digits.startsWith('65') ||
    (digits.length >= 3 && firstThree >= 644 && firstThree <= 649)
  ) {
    return 'discover'
  }
  return null
}

export function validateCreditCardNumber(
  value: string,
  allowedTypes: ReadonlySet<CreditCardType> | null,
  messages: CreditCardFormMessages,
): string | null {
  const digits = digitsOnly(value)
  const type = detectCreditCardType(digits)
  if (type === null) {
    return passesLuhn(digits) && digits.length >= 15
      ? messages.cardTypeUnsupported
      : messages.cardNumberInvalid
  }
  if (allowedTypes !== null && !allowedTypes.has(type)) {
    return messages.cardTypeUnsupported
  }
  if (digits.length !== CARD_LENGTHS[type] || !passesLuhn(digits)) {
    return messages.cardNumberInvalid
  }
  return null
}

export function validateCreditCardExpiry(
  value: string,
  messages: CreditCardFormMessages,
  now: Date = new Date(),
): string | null {
  const digits = digitsOnly(value)
  if (digits.length === 0) return messages.expiryMissing
  if (digits.length !== 4) return messages.expiryInvalid

  const month = Number(digits.slice(0, 2))
  const year = 2000 + Number(digits.slice(2))
  const currentMonth = now.getMonth() + 1
  const currentYear = now.getFullYear()
  if (
    month < 1 ||
    month > 12 ||
    year < currentYear ||
    (year === currentYear && month < currentMonth)
  ) {
    return messages.expiryInvalid
  }
  return null
}

export function validateCreditCardCvv(
  value: string,
  cardType: CreditCardType | null,
  messages: CreditCardFormMessages,
): string | null {
  const digits = digitsOnly(value)
  if (digits.length === 0) return messages.cvvMissing
  const expectedLength = cardType === 'amex' ? 4 : 3
  if (digits.length !== expectedLength) {
    return expectedLength === 4
      ? messages.cvvInvalidFourDigits
      : messages.cvvInvalidThreeDigits
  }
  return null
}

export function validateCreditCardZip(
  value: string,
  messages: CreditCardFormMessages,
): string | null {
  const digits = digitsOnly(value)
  if (digits.length === 0) return messages.zipMissing
  return digits.length === 5 ? null : messages.zipInvalid
}

export function passesLuhn(value: string): boolean {
  const digits = digitsOnly(value)
  if (digits.length < 13 || !/[1-9]/.test(digits)) return false
  let total = 0
  let doubleDigit = false
  for (let index = digits.length - 1; index >= 0; index -= 1) {
    let digit = Number(digits[index])
    if (doubleDigit) {
      digit *= 2
      if (digit > 9) digit -= 9
    }
    total += digit
    doubleDigit = !doubleDigit
  }
  return total % 10 === 0
}

export function digitsOnly(value: string): string {
  return value.replace(/\D/g, '')
}
